import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  message,
  Switch,
  Space,
  Modal,
  Popconfirm,
  Tag,
  Tooltip,
  Collapse,
  Table,
  Spin,
  Empty,
  Upload,
  Avatar,
  Slider,
  Divider,
  Typography,
  Badge,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  SettingOutlined,
  UploadOutlined,
  HolderOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getImageModelListUnified } from '../../graphql/queries';
import {
  updateImageCreditConfigUnified,
  getSignedUrl,
  getObjectUrl,
} from '../../graphql/mutation';
import { uploadToR2 } from '../../utils/uploadToR2';

const { Title } = Typography;
const { Option } = Select;

const AspectRatioEnum = {
  WIDESCREEN: { name: 'Widescreen', ratio: '16:9', x: 16, y: 9 },
  PORTRAIT:   { name: 'Portrait',   ratio: '9:16', x: 9,  y: 16 },
  SQUARE:     { name: 'Square',     ratio: '1:1',  x: 1,  y: 1  },
  FULL_SCREEN:{ name: 'Full Screen',ratio: '4:3',  x: 4,  y: 3  },
  TABLET:     { name: 'Tablet',     ratio: '3:4',  x: 3,  y: 4  },
  ULTRAWIDE:  { name: 'Ultrawide',  ratio: '21:9', x: 21, y: 9  },
};

const ImageResolutionEnum = {
  '0.5K': { name: '0.5K', shortName: '0.5K' },
  '1K':   { name: '1K',   shortName: '1K'   },
  '2K':   { name: '2K',   shortName: '2K'   },
  '4K':   { name: '4K',   shortName: '4K'   },
};

const IMAGE_RESOLUTION_ORDER = ['0.5K', '1K', '2K', '4K'];

const SortableModelWrapper = ({ id, children }) => {
  const {
    attributes, listeners, setNodeRef, setActivatorNodeRef,
    transform, transition, isDragging,
  } = useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        display: 'flex',
        alignItems: 'stretch',
        marginBottom: 16,
        border: '1px solid #d9d9d9',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      <div
        ref={setActivatorNodeRef}
        {...listeners}
        {...attributes}
        title="Drag to reorder"
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0 8px',
          cursor: isDragging ? 'grabbing' : 'grab',
          color: '#bfbfbf',
          background: '#fafafa',
          fontSize: 16,
          flexShrink: 0,
        }}
      >
        <HolderOutlined />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
};

const EMPTY_MODEL_DIALOG = {
  visible: false,
  modelId: '',
  modelName: '',
  modelDescription: '',
  badge: '',
  modelEndpoint: '',
  icon: '',
  iconFile: null,
  costPreview: 1,
  hasResolution: false,
  isFreeCropImage: false,
  pricingRows: [], // [{ resKey: string|null, actualCost: number|null }]
};

export const UnifiedImageCreditConfig = () => {
  const [unifiedConfig, setUnifiedConfig] = useState({ models: [] });
  const [addModelDialog, setAddModelDialog] = useState(EMPTY_MODEL_DIALOG);
  const [editModelDialog, setEditModelDialog] = useState({ ...EMPTY_MODEL_DIALOG, modelIndex: -1 });

  const modelOrderRef = useRef(
    (() => {
      try { return JSON.parse(localStorage.getItem('unifiedImageModelOrder')) || null; } catch { return null; }
    })()
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const { data, loading, refetch } = useQuery(getImageModelListUnified);
  const [updateMutation] = useMutation(updateImageCreditConfigUnified);
  const [getSignedUrlMutation] = useMutation(getSignedUrl);
  const [getObjectUrlMutation] = useMutation(getObjectUrl);

  const saveOrder = (ids) => {
    modelOrderRef.current = ids;
    try { localStorage.setItem('unifiedImageModelOrder', JSON.stringify(ids)); } catch {}
  };

  const applyOrder = (models) => {
    const order = modelOrderRef.current;
    if (!order || !order.length) return models;
    return [...models].sort((a, b) => {
      const ai = order.indexOf(a.id);
      const bi = order.indexOf(b.id);
      return (ai === -1 ? 9999 : ai) - (bi === -1 ? 9999 : bi);
    });
  };

  useEffect(() => {
    if (data?.getImageModelListUnified) {
      const raw = JSON.parse(JSON.stringify(data.getImageModelListUnified));
      setUnifiedConfig({ models: applyOrder(raw.models || []) });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleModelDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setUnifiedConfig((prev) => {
      const oldIdx = prev.models.findIndex((m) => m.id === active.id);
      const newIdx = prev.models.findIndex((m) => m.id === over.id);
      if (oldIdx === -1 || newIdx === -1) return prev;
      const reordered = arrayMove(prev.models, oldIdx, newIdx);
      saveOrder(reordered.map((m) => m.id));
      return { models: reordered };
    });
  };

  // ---- Icon upload ----

  const uploadIcon = async (iconFile) => {
    const { data: signedUrlData } = await getSignedUrlMutation({
      variables: { fileName: iconFile.name },
    });
    const uploadResult = await uploadToR2(
      iconFile,
      signedUrlData.getSignedUrl.url,
      signedUrlData.getSignedUrl.contentType
    );
    if (!uploadResult.success) throw new Error(uploadResult.message);
    const { data: objectUrlData } = await getObjectUrlMutation({
      variables: { key: signedUrlData.getSignedUrl.id, eTag: 'etag' },
    });
    return objectUrlData.getObjectUrl;
  };

  // ---- Pricing helpers ----

  const buildPricingMap = (rows) => {
    const map = {};
    (rows || []).forEach(row => {
      if (!row.resKey) return;
      map[row.resKey] = row.actualCost ?? 0;
    });
    return map;
  };

  const pricingMapToRows = (map) =>
    Object.entries(map || {}).map(([resKey, actualCost]) => ({ resKey, actualCost }));

  // ---- CRUD: Models ----

  const handleAddModel = async () => {
    const d = addModelDialog;
    if (!d.modelId || !d.modelName) {
      message.error('Please fill in Model ID and Model Name');
      return;
    }
    try {
      let iconUrl = d.icon;
      if (d.iconFile) iconUrl = await uploadIcon(d.iconFile);

      const newModel = {
        id: d.modelId,
        name: d.modelName,
        description: d.modelDescription || '',
        badge: d.badge || '',
        model_endpoint: d.modelEndpoint || '',
        icon: iconUrl || '',
        cost_preview: d.costPreview || 1,
        has_resolution: d.hasResolution,
        is_free_crop_image: d.isFreeCropImage,
        pricing_by_resolution: d.hasResolution ? buildPricingMap(d.pricingRows) : null,
        aspect_ratios: [],
      };

      setUnifiedConfig((prev) => ({ models: [...prev.models, newModel] }));
      setAddModelDialog(EMPTY_MODEL_DIALOG);
      message.success('Model added');
    } catch (err) {
      message.error('Failed to add model: ' + err.message);
    }
  };

  const handleEditModel = async () => {
    const d = editModelDialog;
    if (!d.modelId || !d.modelName) {
      message.error('Please fill in Model ID and Model Name');
      return;
    }
    try {
      let iconUrl = d.icon;
      if (d.iconFile) iconUrl = await uploadIcon(d.iconFile);

      setUnifiedConfig((prev) => {
        const newModels = JSON.parse(JSON.stringify(prev.models));
        const model = newModels[d.modelIndex];
        const newPricing = d.hasResolution ? buildPricingMap(d.pricingRows) : null;

        newModels[d.modelIndex] = {
          ...model,
          id: d.modelId,
          name: d.modelName,
          description: d.modelDescription || '',
          badge: d.badge || '',
          model_endpoint: d.modelEndpoint || '',
          icon: iconUrl || '',
          cost_preview: d.costPreview || 1,
          has_resolution: d.hasResolution,
          is_free_crop_image: d.isFreeCropImage,
          pricing_by_resolution: newPricing,
        };

        // Sync actual_cost in existing nested resolutions with new pricing map
        if (newPricing) {
          newModels[d.modelIndex].aspect_ratios?.forEach(ar => {
            ar.resolutions?.forEach(res => {
              const resKey = res.resolution?.api_enum_value;
              if (resKey && newPricing[resKey] !== undefined) {
                res.actual_cost = newPricing[resKey];
              }
            });
          });
        }

        return { models: newModels };
      });

      setEditModelDialog({ ...EMPTY_MODEL_DIALOG, modelIndex: -1 });
      message.success('Model updated');
    } catch (err) {
      message.error('Failed to update model: ' + err.message);
    }
  };

  const handleRemoveModel = (modelIndex) => {
    setUnifiedConfig((prev) => ({
      models: prev.models.filter((_, i) => i !== modelIndex),
    }));
  };

  // ---- CRUD: Aspect Ratios ----

  const handleToggleAspectRatioGlobal = (modelIndex, ratioKey) => {
    const model = unifiedConfig.models[modelIndex];
    const activeKeys = new Set(model.aspect_ratios?.map(ar => ar.ratio?.api_enum_value) || []);

    if (activeKeys.has(ratioKey)) {
      setUnifiedConfig((prev) => {
        const nm = JSON.parse(JSON.stringify(prev.models));
        nm[modelIndex].aspect_ratios = nm[modelIndex].aspect_ratios.filter(
          ar => ar.ratio?.api_enum_value !== ratioKey
        );
        return { models: nm };
      });
    } else {
      const ratioInfo = AspectRatioEnum[ratioKey];
      if (!ratioInfo) return;
      // Copy resolutions from first existing aspect ratio (template)
      const templateResolutions = model.has_resolution && model.aspect_ratios?.[0]?.resolutions
        ? JSON.parse(JSON.stringify(model.aspect_ratios[0].resolutions))
        : [];
      const newRatio = {
        ratio: {
          name: ratioInfo.ratio,
          short_name: ratioInfo.ratio,
          api_enum_value: ratioKey,
          aspect_ratio_x: ratioInfo.x,
          aspect_ratio_y: ratioInfo.y,
        },
        actual_cost: 0,
        cost: 0,
        resolutions: templateResolutions,
      };
      setUnifiedConfig((prev) => {
        const nm = JSON.parse(JSON.stringify(prev.models));
        nm[modelIndex].aspect_ratios = [...(nm[modelIndex].aspect_ratios || []), newRatio];
        return { models: nm };
      });
    }
  };

  // ---- CRUD: Resolutions (global toggle across all aspect ratios) ----

  const handleToggleResolutionGlobal = (modelIndex, resKey) => {
    const model = unifiedConfig.models[modelIndex];

    // Compute active resolutions: present in ALL aspect ratios
    const allResKeys = new Set();
    model.aspect_ratios?.forEach(ar =>
      ar.resolutions?.forEach(r => allResKeys.add(r.resolution?.api_enum_value))
    );
    const activeResolutionKeys = new Set();
    allResKeys.forEach(rk => {
      if (
        model.aspect_ratios?.length > 0 &&
        model.aspect_ratios.every(ar => ar.resolutions?.some(r => r.resolution?.api_enum_value === rk))
      ) activeResolutionKeys.add(rk);
    });

    if (activeResolutionKeys.has(resKey)) {
      setUnifiedConfig((prev) => {
        const nm = JSON.parse(JSON.stringify(prev.models));
        nm[modelIndex].aspect_ratios?.forEach(ar => {
          ar.resolutions = (ar.resolutions || []).filter(r => r.resolution?.api_enum_value !== resKey);
        });
        return { models: nm };
      });
    } else {
      const resInfo = ImageResolutionEnum[resKey];
      if (!resInfo) return;
      const savedCost = (model.pricing_by_resolution || {})[resKey];
      const newRes = {
        resolution: { api_enum_value: resKey, name: resInfo.name },
        actual_cost: savedCost !== undefined ? savedCost : 0,
        cost: 0,
      };
      setUnifiedConfig((prev) => {
        const nm = JSON.parse(JSON.stringify(prev.models));
        nm[modelIndex].aspect_ratios?.forEach(ar => {
          if (!ar.resolutions?.some(r => r.resolution?.api_enum_value === resKey)) {
            ar.resolutions = [...(ar.resolutions || []), newRes];
          }
        });
        return { models: nm };
      });
    }
  };

  // ---- Cost change handlers ----

  const handleAspectRatioCostChange = (modelIndex, ratioIndex, value) => {
    setUnifiedConfig((prev) => {
      const nm = JSON.parse(JSON.stringify(prev.models));
      nm[modelIndex].aspect_ratios[ratioIndex].actual_cost = value ?? 0;
      return { models: nm };
    });
  };

  const handleResolutionCostChange = (modelIndex, ratioIndex, resIndex, value) => {
    setUnifiedConfig((prev) => {
      const nm = JSON.parse(JSON.stringify(prev.models));
      const res = nm[modelIndex].aspect_ratios[ratioIndex].resolutions[resIndex];
      res.actual_cost = value ?? 0;
      // Keep pricing_by_resolution in sync
      const resKey = res.resolution?.api_enum_value;
      if (resKey && nm[modelIndex].pricing_by_resolution) {
        nm[modelIndex].pricing_by_resolution[resKey] = value ?? 0;
      }
      return { models: nm };
    });
  };

  const handleRemoveResolution = (modelIndex, ratioIndex, resIndex) => {
    setUnifiedConfig((prev) => {
      const nm = JSON.parse(JSON.stringify(prev.models));
      nm[modelIndex].aspect_ratios[ratioIndex].resolutions.splice(resIndex, 1);
      return { models: nm };
    });
  };

  // ---- Save ----

  const handleSave = async () => {
    try {
      saveOrder(unifiedConfig.models.map((m) => m.id));
      await updateMutation({ variables: { creditJson: unifiedConfig } });
      message.success('Saving...');
      const refreshed = await refetch();
      if (refreshed?.data?.getImageModelListUnified) {
        const raw = JSON.parse(JSON.stringify(refreshed.data.getImageModelListUnified));
        setUnifiedConfig({ models: applyOrder(raw.models || []) });
      }
      message.success('Unified image configuration saved & costs recalculated.');
    } catch (err) {
      message.error('Save failed: ' + err.message);
    }
  };

  // ---- Model form (shared for Add & Edit) ----

  const renderModelForm = (dialogState, setDialogState) => (
    <Form layout="vertical" style={{ marginTop: 16 }}>
      <Form.Item label="Model ID" required>
        <Input
          value={dialogState.modelId}
          onChange={(e) => setDialogState((prev) => ({ ...prev, modelId: e.target.value }))}
          placeholder="e.g., seedance-1.5"
        />
      </Form.Item>
      <Form.Item label="Model Name" required>
        <Input
          value={dialogState.modelName}
          onChange={(e) => setDialogState((prev) => ({ ...prev, modelName: e.target.value }))}
          placeholder="e.g., Seedance 1.5"
        />
      </Form.Item>
      <Form.Item label="Model Endpoint">
        <Input
          value={dialogState.modelEndpoint}
          onChange={(e) => setDialogState((prev) => ({ ...prev, modelEndpoint: e.target.value }))}
          placeholder="e.g., https://api.example.com/v1/generate (optional)"
        />
      </Form.Item>
      <Form.Item label="Model Icon">
        {dialogState.icon && (
          <div style={{ marginBottom: 8 }}>
            <img
              src={dialogState.icon}
              alt="Current icon"
              style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 4, border: '1px solid #d9d9d9' }}
            />
            <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>Current icon</div>
          </div>
        )}
        <Upload
          accept="image/*,image/svg+xml"
          maxCount={1}
          beforeUpload={(file) => {
            const isImage = file.type.startsWith('image/') || file.type === 'image/svg+xml';
            if (!isImage) { message.error('Only image files allowed!'); return Upload.LIST_IGNORE; }
            if (file.size / 1024 / 1024 >= 5) { message.error('Max 5MB'); return Upload.LIST_IGNORE; }
            setDialogState((prev) => ({ ...prev, iconFile: file }));
            return false;
          }}
          onRemove={() => setDialogState((prev) => ({ ...prev, iconFile: null }))}
        >
          <Button icon={<UploadOutlined />}>
            {dialogState.icon ? 'Change Icon' : 'Upload Icon (Optional)'}
          </Button>
        </Upload>
      </Form.Item>
      <Form.Item label="Cost Preview">
        <Slider
          min={1} max={5}
          value={dialogState.costPreview || 1}
          onChange={(val) => setDialogState((prev) => ({ ...prev, costPreview: val }))}
          marks={{ 1: '$', 2: '$$', 3: '$$$', 4: '$$$$', 5: '$$$$$' }}
        />
      </Form.Item>
      <Form.Item label="Badge">
        <Input
          value={dialogState.badge}
          onChange={(e) => setDialogState((prev) => ({ ...prev, badge: e.target.value }))}
          placeholder="e.g., New, Popular (optional)"
        />
      </Form.Item>
      <Form.Item label="Description">
        <Input.TextArea
          value={dialogState.modelDescription}
          rows={3}
          onChange={(e) => setDialogState((prev) => ({ ...prev, modelDescription: e.target.value }))}
          placeholder="Brief description of the model (optional)"
        />
      </Form.Item>
      <Divider />
      <Form.Item label="Supports Resolution Tiers (1K / 2K / 4K)" extra="Enable if this model has different pricing per resolution tier">
        <Switch
          checked={dialogState.hasResolution}
          onChange={(val) => setDialogState((prev) => ({ ...prev, hasResolution: val, pricingRows: [] }))}
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>
      <Form.Item label="Free Crop Image" extra="Enable free crop for image input">
        <Switch
          checked={dialogState.isFreeCropImage}
          onChange={(val) => setDialogState((prev) => ({ ...prev, isFreeCropImage: val }))}
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>

      {dialogState.hasResolution && (
        <Form.Item label="Resolution Pricing ($/image)">
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            style={{ marginBottom: 12 }}
            onClick={() => setDialogState(prev => ({
              ...prev,
              pricingRows: [...(prev.pricingRows || []), { resKey: null, actualCost: null }],
            }))}
          >
            Add Resolution Row
          </Button>
          {(dialogState.pricingRows || []).length > 0 && (
            <Table
              dataSource={dialogState.pricingRows}
              rowKey={(_, i) => i}
              pagination={false}
              size="small"
              columns={[
                {
                  title: 'Resolution',
                  key: 'resolution',
                  render: (_, row, i) => {
                    const usedKeys = new Set((dialogState.pricingRows || []).map(r => r.resKey).filter(Boolean));
                    return (
                      <Select
                        size="small"
                        style={{ width: '100%' }}
                        value={row.resKey}
                        placeholder="Select resolution"
                        onChange={(val) => setDialogState(prev => {
                          const rows = JSON.parse(JSON.stringify(prev.pricingRows));
                          rows[i] = { ...rows[i], resKey: val };
                          return { ...prev, pricingRows: rows };
                        })}
                      >
                        {IMAGE_RESOLUTION_ORDER.map(key => (
                          <Option key={key} value={key} disabled={usedKeys.has(key) && key !== row.resKey}>
                            {ImageResolutionEnum[key]?.name || key}
                          </Option>
                        ))}
                      </Select>
                    );
                  },
                },
                {
                  title: '$/image',
                  key: 'actualCost',
                  width: 130,
                  render: (_, row, i) => (
                    <InputNumber
                      size="small" min={0} step={0.001} style={{ width: '100%' }}
                      value={row.actualCost}
                      onChange={(val) => setDialogState(prev => {
                        const rows = JSON.parse(JSON.stringify(prev.pricingRows));
                        rows[i] = { ...rows[i], actualCost: val };
                        return { ...prev, pricingRows: rows };
                      })}
                    />
                  ),
                },
                {
                  title: '',
                  key: 'action',
                  width: 40,
                  render: (_, __, i) => (
                    <Button
                      danger size="small" type="text" icon={<DeleteOutlined />}
                      onClick={() => setDialogState(prev => ({
                        ...prev,
                        pricingRows: prev.pricingRows.filter((_, idx) => idx !== i),
                      }))}
                    />
                  ),
                },
              ]}
            />
          )}
        </Form.Item>
      )}

    </Form>
  );

  // ---- Render ----

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
        <p style={{ marginTop: 16 }}>Loading configuration...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1600px' }}>
      <div>
        <Title level={2} style={{ color: 'black', margin: 0, marginBottom: 8 }}>
          Unified Image Credit Configuration
        </Title>
      </div>

      <Card
        title={<span style={{ fontSize: 18, fontWeight: 600 }}>Models</span>}
        extra={
          <Space>
            <Button icon={<SaveOutlined />} onClick={handleSave}>
              Save Changes
            </Button>
            <Button
              icon={<PlusOutlined />}
              onClick={() => setAddModelDialog({ ...EMPTY_MODEL_DIALOG, visible: true })}
            >
              Add Model
            </Button>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        {!unifiedConfig.models || unifiedConfig.models.length === 0 ? (
          <Empty description="No models configured yet" style={{ padding: '40px' }} />
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleModelDragEnd}>
            <SortableContext items={unifiedConfig.models.map((m) => m.id)} strategy={verticalListSortingStrategy}>
              {unifiedConfig.models.map((model, modelIndex) => (
                <SortableModelWrapper key={model.id} id={model.id}>
                  <Collapse
                    style={{ flex: 1, border: 'none', borderRadius: 0 }}
                    expandIconPosition="end"
                    items={[{
                      key: '0',
                      label: (
                        <div>
                          <Space align="start">
                            {model.icon ? (
                              <img
                                src={model.icon}
                                alt={model.name}
                                style={{ width: 48, height: 48, borderRadius: 4, objectFit: 'cover', backgroundColor: '#f0f0f0', border: '1px solid #d9d9d9' }}
                              />
                            ) : (
                              <Avatar size={48} shape="square" style={{ marginTop: 4, backgroundColor: '#f0f0f0' }} icon={<SettingOutlined />} />
                            )}
                            <div style={{ flex: 1 }}>
                              <Space wrap>
                                <Badge status="processing" color="#52c41a" />
                                <strong>{model.name}</strong>
                                <Tag color="purple">{model.id}</Tag>
                                {model.badge && <Tag color="magenta">{model.badge}</Tag>}
                                {model.has_resolution && <Tag color="cyan">Resolution Tiers</Tag>}
                                {model.is_free_crop_image && <Tag color="green">Free Crop</Tag>}
                                {model.cost_preview && <Tag color="cyan">{'$'.repeat(model.cost_preview)}</Tag>}
                              </Space>
                              {model.description && (
                                <div style={{ marginTop: 8, fontSize: 13, fontWeight: 'normal', color: '#666' }}>
                                  {model.description}
                                </div>
                              )}
                              {model.model_endpoint && (
                                <div style={{ marginTop: 6, fontSize: 12, fontWeight: 'normal', color: '#999' }}>
                                  <strong>Endpoint:</strong> {model.model_endpoint}
                                </div>
                              )}
                            </div>
                          </Space>
                        </div>
                      ),
                      extra: (
                        <Space>
                          <Tooltip title="Edit Model Settings">
                            <Button
                              type="default"
                              size="small"
                              icon={<SettingOutlined />}
                              onClick={() => {
                                setEditModelDialog({
                                  visible: true,
                                  modelIndex,
                                  modelId: model.id,
                                  modelName: model.name,
                                  modelDescription: model.description || '',
                                  badge: model.badge || '',
                                  modelEndpoint: model.model_endpoint || '',
                                  icon: model.icon || '',
                                  iconFile: null,
                                  costPreview: model.cost_preview || 1,
                                  hasResolution: model.has_resolution === true,
                                  isFreeCropImage: model.is_free_crop_image === true,
                                  pricingRows: pricingMapToRows(model.pricing_by_resolution),
                                });
                              }}
                            >
                              Edit
                            </Button>
                          </Tooltip>
                          <Popconfirm
                            title="Remove Model"
                            description={`Remove ${model.name}?`}
                            onConfirm={() => handleRemoveModel(modelIndex)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button danger size="small" icon={<DeleteOutlined />}>Remove</Button>
                          </Popconfirm>
                        </Space>
                      ),
                      children: (() => {
                        const activeRatioKeys = new Set(model.aspect_ratios?.map(ar => ar.ratio?.api_enum_value) || []);

                        // Compute active resolution keys (present in ALL aspect ratios)
                        const allResKeys = new Set();
                        model.aspect_ratios?.forEach(ar => ar.resolutions?.forEach(r => allResKeys.add(r.resolution?.api_enum_value)));
                        const activeResKeys = new Set();
                        allResKeys.forEach(rk => {
                          if (model.aspect_ratios?.length > 0 && model.aspect_ratios.every(ar => ar.resolutions?.some(r => r.resolution?.api_enum_value === rk)))
                            activeResKeys.add(rk);
                        });

                        return (
                          <>
                            {/* Toggle section */}
                            <div style={{ marginBottom: 16 }}>
                              <div style={{ marginBottom: 12 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: '#8c8c8c', letterSpacing: 1, marginBottom: 8 }}>ASPECT RATIOS</div>
                                <Space wrap size={8}>
                                  {Object.entries(AspectRatioEnum).map(([key, val]) => (
                                    <Button
                                      key={key}
                                      size="small"
                                      onClick={() => handleToggleAspectRatioGlobal(modelIndex, key)}
                                      style={activeRatioKeys.has(key) ? { borderColor: '#1677ff', color: '#1677ff', background: '#e6f4ff' } : {}}
                                    >
                                      {val.ratio}
                                    </Button>
                                  ))}
                                </Space>
                              </div>

                              {model.has_resolution && (
                                <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #f0f0f0' }}>
                                  <div style={{ fontSize: 11, fontWeight: 600, color: '#8c8c8c', letterSpacing: 1, marginBottom: 8 }}>RESOLUTIONS</div>
                                  <Space wrap size={8}>
                                    {IMAGE_RESOLUTION_ORDER.map(key => (
                                      <Button
                                        key={key}
                                        size="small"
                                        onClick={() => handleToggleResolutionGlobal(modelIndex, key)}
                                        style={activeResKeys.has(key) ? { borderColor: '#52c41a', color: '#52c41a', background: '#f6ffed' } : {}}
                                      >
                                        {ImageResolutionEnum[key].shortName}
                                      </Button>
                                    ))}
                                  </Space>
                                </div>
                              )}
                            </div>

                            {/* Aspect Ratios */}
                            {model.aspect_ratios && model.aspect_ratios.length > 0 && (
                              model.has_resolution ? (
                                /* Resolution-based: collapsible per aspect ratio */
                                <Collapse
                                  accordion
                                  expandIconPosition="end"
                                  items={model.aspect_ratios.map((ar, ratioIndex) => ({
                                    key: ratioIndex,
                                    label: (
                                      <Space>
                                        <Badge status="processing" color="#1890ff" />
                                        <strong>{AspectRatioEnum[ar.ratio?.api_enum_value]?.name || ar.ratio?.api_enum_value}</strong>
                                        <Tag>{ar.ratio?.name}</Tag>
                                      </Space>
                                    ),
                                    extra: (
                                      <Popconfirm
                                        title="Remove Aspect Ratio"
                                        description={`Remove ${ar.ratio?.name}?`}
                                        onConfirm={(e) => { e.stopPropagation(); handleToggleAspectRatioGlobal(modelIndex, ar.ratio?.api_enum_value); }}
                                        okText="Yes"
                                        cancelText="No"
                                        onCancel={(e) => e.stopPropagation()}
                                      >
                                        <Button danger size="small" icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()}>Remove</Button>
                                      </Popconfirm>
                                    ),
                                    children: (
                                      <>
                                        {(!ar.resolutions || ar.resolutions.length === 0) ? (
                                          <Empty description="No resolutions added yet. Use the toggle buttons above." />
                                        ) : (
                                          <Table
                                            dataSource={ar.resolutions}
                                            rowKey={(_, idx) => idx}
                                            pagination={false}
                                            size="small"
                                            columns={[
                                              {
                                                title: 'Resolution',
                                                key: 'resolution',
                                                render: (_, record) => (
                                                  <Space>
                                                    <Tag color="geekblue">{record.resolution?.api_enum_value}</Tag>
                                                    <span>{record.resolution?.name}</span>
                                                  </Space>
                                                ),
                                              },
                                              {
                                                title: 'Actual Cost ($/image)',
                                                key: 'actual_cost',
                                                width: 180,
                                                render: (_, record, resIndex) => (
                                                  <InputNumber
                                                    value={record.actual_cost ?? 0}
                                                    onChange={(val) => handleResolutionCostChange(modelIndex, ratioIndex, resIndex, val)}
                                                    min={0}
                                                    step={0.001}
                                                    style={{ width: '100%' }}
                                                    prefix="$"
                                                  />
                                                ),
                                              },
                                              {
                                                title: 'Credit Cost',
                                                key: 'cost',
                                                width: 150,
                                                render: (_, record) => (
                                                  <InputNumber value={record.cost ?? 0} disabled min={0} style={{ width: '100%' }} prefix={<DollarOutlined />} />
                                                ),
                                              },
                                              {
                                                title: 'Action',
                                                key: 'action',
                                                width: 100,
                                                render: (_, __, resIndex) => (
                                                  <Popconfirm
                                                    title="Remove this resolution?"
                                                    onConfirm={() => handleRemoveResolution(modelIndex, ratioIndex, resIndex)}
                                                    okText="Yes"
                                                    cancelText="No"
                                                  >
                                                    <Button danger size="small" icon={<DeleteOutlined />}>Remove</Button>
                                                  </Popconfirm>
                                                ),
                                              },
                                            ]}
                                          />
                                        )}
                                        {(() => {
                                          const existingKeys = new Set(ar.resolutions?.map(r => r.resolution?.api_enum_value) || []);
                                          const available = IMAGE_RESOLUTION_ORDER.filter(k => !existingKeys.has(k));
                                          if (available.length === 0) return null;
                                          return (
                                            <Select
                                              size="small"
                                              style={{ width: '100%', marginTop: 8 }}
                                              placeholder="+ Add resolution to this aspect ratio..."
                                              value={null}
                                              onChange={(resKey) => {
                                                const resInfo = ImageResolutionEnum[resKey];
                                                const savedCost = (model.pricing_by_resolution || {})[resKey];
                                                const newRes = {
                                                  resolution: { api_enum_value: resKey, name: resInfo.name },
                                                  actual_cost: savedCost !== undefined ? savedCost : 0,
                                                  cost: 0,
                                                };
                                                setUnifiedConfig((prev) => {
                                                  const nm = JSON.parse(JSON.stringify(prev.models));
                                                  const resArr = nm[modelIndex].aspect_ratios[ratioIndex].resolutions;
                                                  nm[modelIndex].aspect_ratios[ratioIndex].resolutions = [...(resArr || []), newRes];
                                                  return { models: nm };
                                                });
                                              }}
                                            >
                                              {available.map(key => (
                                                <Option key={key} value={key}>{ImageResolutionEnum[key].name} — {key}</Option>
                                              ))}
                                            </Select>
                                          );
                                        })()}
                                      </>
                                    ),
                                  }))}
                                />
                              ) : (
                                /* Flat pricing: plain table, no accordion */
                                <Table
                                  dataSource={model.aspect_ratios}
                                  rowKey={(_, idx) => idx}
                                  pagination={false}
                                  size="small"
                                  columns={[
                                    {
                                      title: 'Aspect Ratio',
                                      key: 'ratio',
                                      render: (_, record) => (
                                        <Space>
                                          <strong>{AspectRatioEnum[record.ratio?.api_enum_value]?.name || record.ratio?.api_enum_value}</strong>
                                          <Tag>{record.ratio?.name}</Tag>
                                        </Space>
                                      ),
                                    },
                                    {
                                      title: 'Actual Cost ($/image)',
                                      key: 'actual_cost',
                                      width: 180,
                                      render: (_, record, ratioIndex) => (
                                        <InputNumber
                                          value={record.actual_cost ?? 0}
                                          onChange={(val) => handleAspectRatioCostChange(modelIndex, ratioIndex, val)}
                                          min={0}
                                          step={0.001}
                                          style={{ width: '100%' }}
                                          prefix="$"
                                        />
                                      ),
                                    },
                                    {
                                      title: 'Credit Cost',
                                      key: 'cost',
                                      width: 150,
                                      render: (_, record) => (
                                        <InputNumber value={record.cost ?? 0} disabled min={0} style={{ width: '100%' }} prefix={<DollarOutlined />} />
                                      ),
                                    },
                                    {
                                      title: 'Action',
                                      key: 'action',
                                      width: 100,
                                      render: (_, record) => (
                                        <Popconfirm
                                          title="Remove Aspect Ratio"
                                          description={`Remove ${record.ratio?.name}?`}
                                          onConfirm={() => handleToggleAspectRatioGlobal(modelIndex, record.ratio?.api_enum_value)}
                                          okText="Yes"
                                          cancelText="No"
                                        >
                                          <Button danger size="small" icon={<DeleteOutlined />}>Remove</Button>
                                        </Popconfirm>
                                      ),
                                    },
                                  ]}
                                />
                              )
                            )}
                          </>
                        );
                      })(),
                    }]}
                  />
                </SortableModelWrapper>
              ))}
            </SortableContext>
          </DndContext>
        )}
      </Card>

      {/* Add Model Modal */}
      <Modal
        title="Add Model"
        open={addModelDialog.visible}
        onOk={handleAddModel}
        onCancel={() => setAddModelDialog(EMPTY_MODEL_DIALOG)}
        width={700}
        destroyOnClose
      >
        {renderModelForm(addModelDialog, setAddModelDialog)}
      </Modal>

      {/* Edit Model Modal */}
      <Modal
        title="Edit Model"
        open={editModelDialog.visible}
        onOk={handleEditModel}
        onCancel={() => setEditModelDialog({ ...EMPTY_MODEL_DIALOG, modelIndex: -1 })}
        width={700}
        destroyOnClose
      >
        {renderModelForm(editModelDialog, setEditModelDialog)}
      </Modal>
    </div>
  );
};

export default UnifiedImageCreditConfig;
